import React, { useState } from 'react';
import { ProductImage } from '../../types';
import './ImageGallery.css';

interface ImageGalleryProps {
  images: ProductImage[];
  productName: string;
  selectedIndex?: number;
  onSelectImage?: (index: number) => void;
}

const PLACEHOLDER_IMAGE = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="500" height="500" viewBox="0 0 500 500"><rect width="500" height="500" fill="%23f8fafc"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="18" fill="%2394a3b8">No Image Available</text></svg>';

const ImageGallery: React.FC<ImageGalleryProps> = ({
  images = [],
  productName,
  selectedIndex,
  onSelectImage,
}) => {
  const [internalIndex, setInternalIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const activeIndex = selectedIndex !== undefined ? selectedIndex : internalIndex;
  const validImages = Array.isArray(images) ? images.filter(img => img && img.image) : [];
  const safeIndex = activeIndex >= 0 && activeIndex < validImages.length ? activeIndex : 0;
  const currentImage = validImages[safeIndex];

  const handleSelect = (idx: number) => {
    setInternalIndex(idx);
    if (onSelectImage) {
      onSelectImage(idx);
    }
  };

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (validImages.length <= 1) return;
    const nextIdx = safeIndex === 0 ? validImages.length - 1 : safeIndex - 1;
    handleSelect(nextIdx);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (validImages.length <= 1) return;
    const nextIdx = safeIndex === validImages.length - 1 ? 0 : safeIndex + 1;
    handleSelect(nextIdx);
  };

  return (
    <>
      <div className="image-gallery">
        {validImages.length > 1 && (
          <div className="thumbnail-list">
            {validImages.map((image, index) => (
              <div
                key={image.id ?? index}
                className={`thumbnail ${index === safeIndex ? 'active' : ''}`}
                onClick={() => handleSelect(index)}
              >
                <img
                  src={image.image}
                  alt={`${productName} thumbnail ${index + 1}`}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).src = PLACEHOLDER_IMAGE; }}
                />
              </div>
            ))}
          </div>
        )}

        <div className="main-image-container">
          <div
            className="main-image"
            onClick={() => setIsLightboxOpen(true)}
            title="Click to view full image"
          >
            <img
              src={currentImage?.image || PLACEHOLDER_IMAGE}
              alt={`${productName} - ${safeIndex + 1}`}
              onError={(e) => { (e.currentTarget as HTMLImageElement).src = PLACEHOLDER_IMAGE; }}
            />

            <button
              type="button"
              className="image-zoom-btn"
              onClick={(e) => {
                e.stopPropagation();
                setIsLightboxOpen(true);
              }}
              title="Full Screen View"
              aria-label="Full Screen View"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
              </svg>
            </button>
          </div>

          {validImages.length > 1 && (
            <>
              <button
                type="button"
                className="nav-btn prev"
                onClick={handlePrev}
                aria-label="Previous image"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
              <button
                type="button"
                className="nav-btn next"
                onClick={handleNext}
                aria-label="Next image"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Full-view Lightbox Modal */}
      {isLightboxOpen && (
        <div
          className="gallery-lightbox-overlay"
          onClick={() => setIsLightboxOpen(false)}
        >
          <div
            className="gallery-lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="gallery-lightbox-close"
              onClick={() => setIsLightboxOpen(false)}
              aria-label="Close full view"
            >
              ✕
            </button>

            <img
              src={currentImage?.image || PLACEHOLDER_IMAGE}
              alt={productName}
              className="gallery-lightbox-img"
            />

            {validImages.length > 1 && (
              <>
                <button
                  type="button"
                  className="gallery-lightbox-nav prev"
                  onClick={handlePrev}
                  aria-label="Previous"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="gallery-lightbox-nav next"
                  onClick={handleNext}
                  aria-label="Next"
                >
                  ›
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ImageGallery;
